import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { AuditChip } from '@/components/audit/AuditChip';
import { ProductForm } from '@/features/commerce/ProductForm';
import { useDesigners, useProducts } from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import { formatCents } from '@/lib/money';
import { useUiStore } from '@/stores/ui';
import {
  productAvailabilitySchema,
  type Product,
  type ProductAvailability,
  type ProductListFilters,
} from '@/schemas/admin/commerce/products';

const searchSchema = z.object({
  q: z.string().optional(),
  designer_id: z.string().uuid().optional(),
  availability: productAvailabilitySchema.optional(),
  trash: z.boolean().optional(),
});

export const Route = createFileRoute('/_authed/commerce/products/')({
  validateSearch: searchSchema,
  component: ProductsListScreen,
});

function ProductsListScreen() {
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const designers = useDesigners();
  const resolveError = useApiError();
  const locale = useUiStore((s) => s.locale);

  const filters: ProductListFilters = React.useMemo(() => {
    const out: ProductListFilters = { limit: 100 };
    if (search.q) out.q = search.q;
    if (search.designer_id) out.designer_id = search.designer_id;
    if (search.availability) out.availability = search.availability;
    if (search.trash) out.include_deleted = true;
    return out;
  }, [search.q, search.designer_id, search.availability, search.trash]);

  const list = useProducts(filters);
  const [creating, setCreating] = React.useState(false);
  const [draftQ, setDraftQ] = React.useState(search.q ?? '');

  React.useEffect(() => {
    const id = window.setTimeout(() => {
      const value = draftQ.trim();
      const current = search.q ?? '';
      if (value === current) return;
      void navigate({
        to: '/commerce/products',
        search: (prev) => ({ ...prev, q: value || undefined }),
        replace: true,
      });
    }, 250);
    return () => window.clearTimeout(id);
  }, [draftQ, navigate, search.q]);

  const trashTab = Boolean(search.trash);
  const data = (list.data ?? []).filter((p) =>
    trashTab ? Boolean(p.deleted_at) : !p.deleted_at
  );

  const columns: readonly DataColumn<Product>[] = [
    {
      id: 'name',
      header: t('products.table.name'),
      cell: (p) => {
        const primary =
          (locale === 'eg-ar' ? p.name_eg_ar : locale === 'ar' ? p.name_ar : p.name_en) ??
          p.name_en;
        const secondary =
          locale === 'en' ? (p.name_ar ?? p.name_eg_ar ?? null) : (p.name_en ?? null);
        return (
          <div className="flex min-w-0 flex-col">
            <Link
              to="/commerce/products/$id"
              params={{ id: p.id }}
              className="truncate font-medium text-[color:var(--color-accent)] hover:underline"
            >
              {primary}
            </Link>
            {secondary ? (
              <span
                dir={locale === 'en' ? 'rtl' : 'ltr'}
                className="truncate text-xs text-[color:var(--color-fg-muted)]"
              >
                {secondary}
              </span>
            ) : null}
          </div>
        );
      },
      sortBy: (p) => p.name_en,
    },
    {
      id: 'designer',
      header: t('products.table.designer'),
      cell: (p) => p.designer_name ?? '—',
      sortBy: (p) => p.designer_name ?? '',
    },
    {
      id: 'availability',
      header: t('products.table.availability'),
      cell: (p) => (
        <Badge tone={availabilityTone(p.availability)}>
          {t(`products.availability.${p.availability}`)}
        </Badge>
      ),
      sortBy: (p) => p.availability,
    },
    {
      id: 'price',
      header: t('products.table.price'),
      cell: (p) => formatCents(p.base_price_cents, locale, p.base_currency),
      align: 'end',
      sortBy: (p) => p.base_price_cents,
    },
    {
      id: 'tags',
      header: t('products.table.tags'),
      cell: (p) =>
        p.tags && p.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {p.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} tone="neutral">
                {tag}
              </Badge>
            ))}
            {p.tags.length > 3 ? (
              <span className="text-xs text-[color:var(--color-fg-muted)]">
                +{p.tags.length - 3}
              </span>
            ) : null}
          </div>
        ) : (
          <span className="text-[color:var(--color-fg-subtle)]">—</span>
        ),
    },
  ];

  function setSearchParam<K extends keyof typeof search>(key: K, value: (typeof search)[K]) {
    void navigate({
      to: '/commerce/products',
      search: (prev) => ({ ...prev, [key]: value }),
      replace: true,
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('products.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('products.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            {t('products.new')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="grow basis-64">
              <label htmlFor="products-q" className="sr-only">
                {t('products.searchLabel')}
              </label>
              <Input
                id="products-q"
                value={draftQ}
                onChange={(e) => setDraftQ(e.target.value)}
                placeholder={t('products.searchPlaceholder')}
                autoComplete="off"
                dir="ltr"
              />
            </div>
            <div className="basis-56">
              <label htmlFor="products-designer" className="sr-only">
                {t('products.designerFilter')}
              </label>
              <select
                id="products-designer"
                value={search.designer_id ?? ''}
                onChange={(e) =>
                  setSearchParam('designer_id', (e.target.value || undefined) as never)
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('products.allDesigners')}</option>
                {(designers.data ?? []).map((d) => {
                  const display =
                    (locale === 'eg-ar' ? d.name_eg_ar : locale === 'ar' ? d.name_ar : d.name_en) ??
                    d.name;
                  return (
                    <option key={d.id} value={d.id}>
                      {display}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="basis-56">
              <label htmlFor="products-availability" className="sr-only">
                {t('products.availabilityFilter')}
              </label>
              <select
                id="products-availability"
                value={search.availability ?? ''}
                onChange={(e) =>
                  setSearchParam(
                    'availability',
                    (e.target.value
                      ? (e.target.value as ProductAvailability)
                      : undefined) as never
                  )
                }
                className="block h-11 w-full rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm text-[color:var(--color-fg)] shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)]"
              >
                <option value="">{t('products.allAvailability')}</option>
                <option value="coming_soon">{t('products.availability.coming_soon')}</option>
                <option value="available">{t('products.availability.available')}</option>
                <option value="sold_out">{t('products.availability.sold_out')}</option>
              </select>
            </div>
          </div>

          <div className="flex gap-2 border-b border-[color:var(--color-border)]">
            <button
              type="button"
              onClick={() => setSearchParam('trash', undefined as never)}
              aria-pressed={!trashTab}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                !trashTab
                  ? 'border-[color:var(--color-accent)] text-[color:var(--color-fg)]'
                  : 'border-transparent text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]'
              }`}
            >
              {t('products.tabs.live')}
            </button>
            <button
              type="button"
              onClick={() => setSearchParam('trash', true)}
              aria-pressed={trashTab}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
                trashTab
                  ? 'border-[color:var(--color-accent)] text-[color:var(--color-fg)]'
                  : 'border-transparent text-[color:var(--color-fg-muted)] hover:text-[color:var(--color-fg)]'
              }`}
            >
              {t('products.tabs.trash')}
            </button>
          </div>

          <DataTable<Product>
            data={data}
            columns={columns}
            rowKey={(p) => p.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{
              title: trashTab ? t('products.empty.trashTitle') : t('products.empty.title'),
              body: trashTab ? t('products.empty.trashBody') : t('products.empty.body'),
            }}
            initialSort={{ id: 'name', direction: 'asc' }}
            onRowClick={(p) => void navigate({ to: '/commerce/products/$id', params: { id: p.id } })}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('products.new')}</DialogTitle>
            <DialogDescription>
              {t('products.newBody', {
                defaultValue:
                  'Create a product draft. Variants, media, and pricing can be added after the product is saved.',
              })}
            </DialogDescription>
          </DialogHeader>
          <ProductForm
            {...(search.designer_id ? { defaultDesignerId: search.designer_id } : {})}
            onSaved={(id) => {
              setCreating(false);
              void navigate({ to: '/commerce/products/$id', params: { id } });
            }}
            onCancel={() => setCreating(false)}
          />
        </DialogContent>
      </Dialog>

      <p className="text-xs text-[color:var(--color-fg-muted)]">{tc('audit.chipHint')}</p>
    </div>
  );
}

function availabilityTone(a: ProductAvailability): 'success' | 'warn' | 'danger' | 'neutral' {
  switch (a) {
    case 'available':
      return 'success';
    case 'coming_soon':
      return 'warn';
    case 'sold_out':
      return 'danger';
    default:
      return 'neutral';
  }
}
